/**
 * Dispatch — outbound prompt delivery (PRD-0050). Trailing slashes per
 * backend routing; the stream endpoint is runner-only and not exposed here.
 */

import type { AgentCliGovernance, AgentCliModel, AgentCliModelUpdatePayload, AgentCliPin, AgentCliPinAuthorisePayload, AgentCliUpdateAttempt } from '@/types/dispatch/agentCli'
import type { AgentTrackRecord, DispatchWorkspace, DispatchWorkspaceBranches, DispatchWorkspaceCreatePayload, DispatchWorkspaceUpdatePayload, WorkspaceMergePolicy, WorkspaceMergePolicyList, WorkspaceMergePolicyUpdatePayload } from '@/types/dispatch/workspaces'
import type { CronPreviewRequest, CronPreviewResponse, DispatchSchedule, DispatchScheduleCreatePayload, DispatchScheduleListResponse, DispatchScheduleUpdatePayload, PromptCapabilityRequest, SchedulePromptCapabilityResponse } from '@/types/dispatch/schedules'
import type { DispatcherAutomationStatus, DispatcherControl, DispatcherControlUpdatePayload } from '@/types/dispatch/automation'
import type { ReportDelivery, ReportDeliveryListResponse } from '@/types/dispatch/reportDeliveries'
import type { DispatcherKind, DispatchCli } from '@/types/dispatch/vocabulary'
import type { DispatcherRoute, DispatcherRouteCreatePayload, DispatcherRouteListResponse, DispatcherRouteTogglePayload, DispatcherRouteUpdatePayload } from '@/types/dispatch/routes'
import type { DispatchArchiveTerminalResponse, DispatchCreatePayload, DispatchDetail, DispatchListFilters, DispatchListResponse } from '@/types/dispatch/records'
import type { DispatchEndpointInfo, DispatchRunnerInfo } from '@/types/dispatch/targets'
import type { DispatchWorkspaceInventoryItem } from '@/types/dispatch/inventory'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

export const dispatch = {
  list: async (filters: DispatchListFilters = {}): Promise<DispatchListResponse> => {
    const response = await apiClient.get<DispatchListResponse>('/api/v1/dispatch/', {
      params: filters,
      paramsSerializer: repeatedKeyParamsSerializer,
    })
    return response.data
  },

  get: async (id: string): Promise<DispatchDetail> => {
    const response = await apiClient.get<DispatchDetail>(`/api/v1/dispatch/${id}/`)
    return response.data
  },

  getByNumber: async (n: number): Promise<DispatchDetail> => {
    const response = await apiClient.get<DispatchDetail>(`/api/v1/dispatch/by-number/${n}/`)
    return response.data
  },

  create: async (data: DispatchCreatePayload): Promise<DispatchDetail> => {
    const response = await apiClient.post<DispatchDetail>('/api/v1/dispatch/', data)
    return response.data
  },

  abort: async (id: string, reason?: string): Promise<DispatchDetail> => {
    const response = await apiClient.post<DispatchDetail>(
      `/api/v1/dispatch/${id}/abort/`,
      reason ? { reason } : {}
    )
    return response.data
  },

  redispatch: async (id: string): Promise<DispatchDetail> => {
    const response = await apiClient.post<DispatchDetail>(
      `/api/v1/dispatch/${id}/redispatch/`
    )
    return response.data
  },

  archive: async (id: string): Promise<DispatchDetail> => {
    const response = await apiClient.post<DispatchDetail>(
      `/api/v1/dispatch/${id}/archive/`
    )
    return response.data
  },

  unarchive: async (id: string): Promise<DispatchDetail> => {
    const response = await apiClient.post<DispatchDetail>(
      `/api/v1/dispatch/${id}/unarchive/`
    )
    return response.data
  },

  archiveTerminal: async (): Promise<DispatchArchiveTerminalResponse> => {
    const response = await apiClient.post<DispatchArchiveTerminalResponse>(
      '/api/v1/dispatch/archive-terminal/'
    )
    return response.data
  },

  endpoints: async (): Promise<DispatchEndpointInfo[]> => {
    const response = await apiClient.get<DispatchEndpointInfo[]>('/api/v1/dispatch/endpoints/')
    return response.data
  },

  retireRunner: async (id: string, reason: string): Promise<DispatchRunnerInfo> => {
    const response = await apiClient.post<DispatchRunnerInfo>(`/api/v1/dispatch/runners/${id}/retire/`, { reason })
    return response.data
  },

  runners: async (includeRetired = false): Promise<DispatchRunnerInfo[]> => {
    const response = await apiClient.get<DispatchRunnerInfo[]>('/api/v1/dispatch/runners/', { params: { include_retired: includeRetired } })
    return response.data
  },

  workspaces: async (): Promise<DispatchWorkspace[]> =>
    (await apiClient.get<DispatchWorkspace[]>('/api/v1/dispatch/workspaces/')).data,

  createWorkspace: async (data: DispatchWorkspaceCreatePayload): Promise<DispatchWorkspace> =>
    (await apiClient.post<DispatchWorkspace>('/api/v1/dispatch/workspaces/', data)).data,

  updateWorkspace: async (
    id: string,
    data: DispatchWorkspaceUpdatePayload,
  ): Promise<DispatchWorkspace> =>
    (await apiClient.put<DispatchWorkspace>(`/api/v1/dispatch/workspaces/${id}/`, data)).data,

  deleteWorkspace: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/dispatch/workspaces/${id}/`)
  },

  workspaceBranches: async (id: string): Promise<DispatchWorkspaceBranches> =>
    (await apiClient.get<DispatchWorkspaceBranches>(`/api/v1/dispatch/workspaces/${id}/branches/`))
      .data,

  agentTrackRecord: async (id: string): Promise<AgentTrackRecord> =>
    (
      await apiClient.get<AgentTrackRecord>(
        `/api/v1/dispatch/workspaces/${id}/agent-track-record/`,
      )
    ).data,

  workspaceInventory: async (runnerId?: string): Promise<DispatchWorkspaceInventoryItem[]> =>
    (
      await apiClient.get<DispatchWorkspaceInventoryItem[]>(
        '/api/v1/dispatch/workspace-inventory/',
        { params: runnerId ? { runner_id: runnerId } : undefined },
      )
    ).data,

  workspaceMergePolicies: async (): Promise<WorkspaceMergePolicyList> =>
    (
      await apiClient.get<WorkspaceMergePolicyList>(
        '/api/v1/dispatch/workspaces/merge-policies/',
      )
    ).data,

  updateWorkspaceMergePolicy: async (
    id: string,
    data: WorkspaceMergePolicyUpdatePayload,
    ifMatch: number,
  ): Promise<WorkspaceMergePolicy> =>
    (
      await apiClient.put<WorkspaceMergePolicy>(
        `/api/v1/dispatch/workspaces/${id}/merge-policy/`,
        data,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  deleteWorkspaceMergePolicy: async (id: string, ifMatch: number): Promise<void> => {
    await apiClient.delete(
      `/api/v1/dispatch/workspaces/${id}/merge-policy/`,
      ifMatchHeaders(ifMatch),
    )
  },

  automationControls: async (): Promise<DispatcherControl[]> =>
    (await apiClient.get<DispatcherControl[]>('/api/v1/dispatch/automation/')).data,

  automationStatus: async (): Promise<DispatcherAutomationStatus> =>
    (
      await apiClient.get<DispatcherAutomationStatus>(
        '/api/v1/dispatch/automation/status/',
      )
    ).data,

  updateAutomationControl: async (
    dispatcher: DispatcherKind,
    body: DispatcherControlUpdatePayload,
    ifMatch: number,
  ): Promise<DispatcherControl> =>
    (
      await apiClient.put<DispatcherControl>(
        `/api/v1/dispatch/automation/${dispatcher}/`,
        body,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  // ── Agent-CLI governance (PRD-0176) ──────────────────────────────────────
  // POST /dispatch/runner/agent-cli-update/ is the relay's own install report
  // and is deliberately absent from this browser-facing surface: it is written
  // by a machine principal only.

  agentCliGovernance: async (): Promise<AgentCliGovernance[]> =>
    (await apiClient.get<AgentCliGovernance[]>('/api/v1/dispatch/agent-clis/')).data,

  // If-Match carries AgentCliPin.pin_version; the body carries a version and a
  // rationale only — the artefact URL and digest are resolved server-side.
  authoriseAgentCli: async (
    cli: DispatchCli,
    body: AgentCliPinAuthorisePayload,
    ifMatch: number,
  ): Promise<AgentCliPin> =>
    (
      await apiClient.put<AgentCliPin>(
        `/api/v1/dispatch/agent-clis/${cli}/`,
        body,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  // DELETE addresses the authorisation, not the row: the response is the pin,
  // still present, at status=withdrawn. Same pin_version lock as authorise.
  withdrawAgentCli: async (cli: DispatchCli, ifMatch: number): Promise<AgentCliPin> =>
    (
      await apiClient.delete<AgentCliPin>(
        `/api/v1/dispatch/agent-clis/${cli}/`,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  listAgentCliModels: async (): Promise<AgentCliModel[]> =>
    (await apiClient.get<AgentCliModel[]>('/api/v1/dispatch/agent-clis/models/')).data,

  // modelId is the row UUID, not the vendor model string: the vocabulary's
  // natural key is (cli_system, model_id) and this path carries no CLI segment,
  // so only the row id addresses it unambiguously. If-Match carries
  // AgentCliModel.model_version, distinct from the pin's.
  updateAgentCliModel: async (
    modelId: string,
    body: AgentCliModelUpdatePayload,
    ifMatch: number,
  ): Promise<AgentCliModel> =>
    (
      await apiClient.put<AgentCliModel>(
        `/api/v1/dispatch/agent-clis/models/${modelId}/`,
        body,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  // No If-Match: an attempt row has no version token, and the route is
  // idempotent — an already-terminal attempt is returned unchanged.
  abandonAgentCliAttempt: async (
    cli: DispatchCli,
    attemptId: string,
  ): Promise<AgentCliUpdateAttempt> =>
    (
      await apiClient.post<AgentCliUpdateAttempt>(
        `/api/v1/dispatch/agent-clis/${cli}/attempts/${attemptId}/abandon/`,
      )
    ).data,

  listSchedules: async (): Promise<DispatchScheduleListResponse> =>
    (await apiClient.get<DispatchScheduleListResponse>('/api/v1/dispatch/schedules/')).data,

  createSchedule: async (data: DispatchScheduleCreatePayload): Promise<DispatchSchedule> =>
    (await apiClient.post<DispatchSchedule>('/api/v1/dispatch/schedules/', data)).data,

  updateSchedule: async (
    id: string,
    data: DispatchScheduleUpdatePayload,
    ifMatch: number,
  ): Promise<DispatchSchedule> =>
    (
      await apiClient.put<DispatchSchedule>(
        `/api/v1/dispatch/schedules/${id}/`,
        data,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  deleteSchedule: async (id: string, ifMatch: number): Promise<void> => {
    await apiClient.delete(`/api/v1/dispatch/schedules/${id}/`, ifMatchHeaders(ifMatch))
  },

  cronPreview: async (cadenceCron: string): Promise<CronPreviewResponse> =>
    (
      await apiClient.post<CronPreviewResponse>('/api/v1/dispatch/schedules/cron-preview/', {
        cadence_cron: cadenceCron,
      } satisfies CronPreviewRequest)
    ).data,

  // A run reports at most once, so the envelope holds its delivery or nothing.
  listReportDeliveries: async (dispatchId: string): Promise<ReportDeliveryListResponse> =>
    (
      await apiClient.get<ReportDeliveryListResponse>('/api/v1/dispatch/report-deliveries/', {
        params: { dispatch_id: dispatchId },
      })
    ).data,

  // No If-Match: the server locks the row and refuses any state but failed,
  // skipped or uncertain, so a retry against a moved delivery is a 409, never a
  // double send.
  retryReportDelivery: async (deliveryId: string): Promise<ReportDelivery> =>
    (
      await apiClient.post<ReportDelivery>(
        `/api/v1/dispatch/report-deliveries/${deliveryId}/retry/`,
      )
    ).data,

  // Fleet-vocabulary resolve for the edit dialog's model/effort dropdowns AND
  // the prompt_run bindings editor — the server is the authority (never
  // client-derived from the prompt's target_cli). Composed, non-optional
  // response body: capability is null for an unresolvable/CLI-agnostic pin,
  // declared_params is [] in that case (PRD-0192 D-2a). skill_coverage judges
  // the prompt's required skills with the role profiles named here, which a
  // lane or route pin leaves out (PRD-0364 D-17).
  schedulePromptCapability: async (
    promptId: string,
    capabilityProfiles?: readonly string[],
  ): Promise<SchedulePromptCapabilityResponse> =>
    (
      await apiClient.post<SchedulePromptCapabilityResponse>(
        '/api/v1/dispatch/schedules/prompt-capability/',
        {
          prompt_id: promptId,
          ...(capabilityProfiles ? { capability_profiles: [...capabilityProfiles] } : {}),
        } satisfies PromptCapabilityRequest,
      )
    ).data,

  // ── Registered implementer routes (PRD-0198 D-13) ────────────────────────
  // The If-Match token on all four mutations is the LANE's control_version,
  // which the server treats as optional (Header(default=None)) — but the UI
  // always holds it from the list response, so every call here sends it via
  // the required-token ifMatchHeaders helper rather than the optional one.

  listDispatcherRoutes: async (): Promise<DispatcherRouteListResponse> =>
    (await apiClient.get<DispatcherRouteListResponse>('/api/v1/dispatch/routes/')).data,

  registerDispatcherRoute: async (
    data: DispatcherRouteCreatePayload,
    ifMatch: number,
  ): Promise<DispatcherRoute> =>
    (
      await apiClient.post<DispatcherRoute>(
        '/api/v1/dispatch/routes/',
        data,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  updateDispatcherRoute: async (
    id: string,
    data: DispatcherRouteUpdatePayload,
    ifMatch: number,
  ): Promise<DispatcherRoute> =>
    (
      await apiClient.put<DispatcherRoute>(
        `/api/v1/dispatch/routes/${id}/`,
        data,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  toggleDispatcherRoute: async (
    id: string,
    data: DispatcherRouteTogglePayload,
    ifMatch: number,
  ): Promise<DispatcherRoute> =>
    (
      await apiClient.post<DispatcherRoute>(
        `/api/v1/dispatch/routes/${id}/toggle/`,
        data,
        ifMatchHeaders(ifMatch),
      )
    ).data,

  deleteDispatcherRoute: async (id: string, ifMatch: number): Promise<void> => {
    await apiClient.delete(`/api/v1/dispatch/routes/${id}/`, ifMatchHeaders(ifMatch))
  },
}
